/** Naturaleza fiscal del vendedor. Es la única entrada del motor: el canal
 *  de compra (subasta, dealer, flota) no determina nunca el régimen, porque
 *  una subasta vende tanto lotes de renting con IVA como coches de particular. */
export const supplierKinds = [
  "private",
  "exempt_business",
  "franchise_business",
  "reseller_rebu",
  "business_general",
] as const;

export type SupplierKind = (typeof supplierKinds)[number];

export const taxFlows = ["rebu", "general", "commission"] as const;
export type TaxFlow = (typeof taxFlows)[number];

export const acquisitionModes = ["owned", "consignment"] as const;
export type AcquisitionMode = (typeof acquisitionModes)[number];

export const supplierKindLabels: Record<SupplierKind, string> = {
  private: "Particular",
  exempt_business: "Empresario con entrega exenta",
  franchise_business: "Empresario en régimen de franquicia",
  reseller_rebu: "Revendedor que aplica REBU",
  business_general: "Empresario en régimen general",
};

const rebuKinds: readonly SupplierKind[] = [
  "private",
  "exempt_business",
  "franchise_business",
  "reseller_rebu",
];

export interface ConformingReclassification {
  supplierKind: SupplierKind;
  flow: TaxFlow;
  hint: string;
}

export type TaxFlowResult =
  | { ok: true; flow: TaxFlow }
  | {
      ok: false;
      code: "ORIGEN_SIN_RESOLVER";
      observed: { supplierKind: SupplierKind; vatDeductible: boolean };
      conforming: readonly ConformingReclassification[];
    };

export interface DetermineTaxFlowInput {
  acquisitionMode: AcquisitionMode;
  supplierKind: SupplierKind;
  vatDeductible: boolean;
}

/**
 * El vehículo en depósito nunca entra en el patrimonio del tenant, así que su
 * flujo es siempre comisión con independencia del proveedor.
 *
 * En propiedad, la contradicción entre la clasificación del proveedor y lo que
 * dice su factura no se resuelve eligiendo: se bloquea y se ofrecen las
 * reclasificaciones que sí encajan con el documento aportado.
 */
export function determineTaxFlow(input: DetermineTaxFlowInput): TaxFlowResult {
  if (input.acquisitionMode === "consignment") return { ok: true, flow: "commission" };

  const observed = { supplierKind: input.supplierKind, vatDeductible: input.vatDeductible };

  if (rebuKinds.includes(input.supplierKind)) {
    if (!input.vatDeductible) return { ok: true, flow: "rebu" };
    return {
      ok: false,
      code: "ORIGEN_SIN_RESOLVER",
      observed,
      conforming: [{
        supplierKind: "business_general",
        flow: "general",
        hint: "La factura de compra desglosa IVA deducible",
      }],
    };
  }

  if (input.vatDeductible) return { ok: true, flow: "general" };

  return {
    ok: false,
    code: "ORIGEN_SIN_RESOLVER",
    observed,
    conforming: [
      {
        supplierKind: "reseller_rebu",
        flow: "rebu",
        hint: "La factura del proveedor menciona el régimen especial de los bienes usados",
      },
      {
        supplierKind: "exempt_business",
        flow: "rebu",
        hint: "Entrega exenta por el artículo 20.Uno.24º o 25º LIVA",
      },
      {
        supplierKind: "franchise_business",
        flow: "rebu",
        hint: "Proveedor acogido al régimen de franquicia",
      },
    ],
  };
}

export type OverrideResult =
  | { ok: true }
  | { ok: false; code: "OVERRIDE_SIN_MOTIVO" | "OVERRIDE_CONTRADICE_DOCUMENTO" };

/**
 * El único override admisible es la renuncia a REBU operación por operación,
 * que la norma permite. Lo contrario (declarar REBU una compra con IVA
 * deducible) produciría una factura ilegal, y el flujo de comisión depende del
 * modo de adquisición del vehículo, no de una decisión del usuario.
 */
export function validateOverride(input: {
  current: TaxFlow;
  target: TaxFlow;
  reason: string;
}): OverrideResult {
  if (input.reason.trim().length === 0) return { ok: false, code: "OVERRIDE_SIN_MOTIVO" };
  if (input.current === "rebu" && input.target === "general") return { ok: true };
  return { ok: false, code: "OVERRIDE_CONTRADICE_DOCUMENTO" };
}
